import { debounce } from 'lodash-es';
import { ConfigurationResponse } from '../../models/configuration';
import {
  AuthorizedClients,
  Basis,
  Connected,
  IsacSession,
} from '../../models/session';
import {
  getUserData,
  libraryState,
  setBasis,
  setCypherCode,
  setSession,
  setUserData,
} from '../../state';
import LocalStorage from '../../store/LocalStorage';
import {
  configurationSignal,
  filesSignal,
  stateSignal,
} from '../../store/StoreContext';
import { UserData, VirtualFileSystemType } from '../../types';
import API_CALL, { ApiError } from '../API';
import { configurationService } from '../configuration';
import { FilesService } from '../files/FilesService';
import InboxService from '../inbox/InboxService';
import { Inbox, ShareService, Shares } from '../share/ShareService';
/** Codici di errore applicativi del login (non hanno uno status HTTP). */
export const LOGIN_ERROR = {
  /** Credenziali valide ma sessione applicativa negata: account sospeso. */
  ACCOUNT_SUSPENDED: 'ACCOUNT_SUSPENDED',
} as const;

/**
 * Initiates the login flow for a user.
 * @param {string} login - The user's login identifier.
 * @param {string} password - The user's password.
 * @returns {Promise<IsacSession>} A promise that resolves to the user's session.
 * @throws {Error} If the login fails.
 */
const loginFlow = async (
  login: string,
  password: string
): Promise<IsacSession> => {
  // Se il token viene emesso le credenziali sono valide: un fallimento dopo
  // questo punto significa che la sessione applicativa e' negata, cioe'
  // account sospeso (stessa deduzione della webapp legacy).
  let credentialsAccepted = false;
  try {
    const token = await loginSession(login, password);
    credentialsAccepted = true;
    LocalStorage.setItem('token', token);

    const isacSession = await checkIsAuthenticated();
    if (isacSession) {
      return isacSession;
    } else {
      throw new ApiError({ code: LOGIN_ERROR.ACCOUNT_SUSPENDED });
    }
  } catch (err) {
    stateSignal.value.isLoggedIn = false;
    console.error('Login failed', err);
    if (credentialsAccepted && !(err instanceof ApiError && err.status)) {
      throw new ApiError({ code: LOGIN_ERROR.ACCOUNT_SUSPENDED });
    }
    throw err;
  }
};

const loginSession = async (
  login: string,
  password: string
): Promise<string> => {
  try {
    return await API_CALL({
      channel: 'isac-rest',
      url: '/session',
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-REQUESTED-FOR': 'authentication',
      },
      data: { login, password },
    });
  } catch (err) {
    console.error('Login failed', err);
    throw err;
  }
};

const getSession = async (): Promise<IsacSession> => {
  try {
    const isacSession = await API_CALL({
      url: '/user',
      method: 'GET',
    });

    setSession(isacSession);

    return isacSession;
  } catch (err) {
    setSession(null);
    console.error('Authentication check failed', err);
    throw new Error('Authentication check failed');
  }
};

const checkIsAuthenticated = async (): Promise<IsacSession | null> => {
  return new Promise(async (resolve, reject) => {
    try {
      const isAlreadyAuthenticated = await isAuthenticated();

      if (isAlreadyAuthenticated) {
        const session = await getSession();

        await getAllData();

        if (session) {
          stateSignal.value.isLoggedIn = true;
          resolve(session);
        } else {
          stateSignal.value.isLoggedIn = false;
          reject(null);
        }
      } else {
        reject(null);
      }
    } catch (err) {
      console.error('Authentication check failed', err);
      reject(null);
    }
  });
};

// Definiamo un enum per i tipi di dati che possiamo refreshare
export enum DataType {
  CONFIGURATION = 'configuration',
  FILES = 'files',
  BASIS = 'basis',
  CONNECTED = 'connected',
  AUTHORIZED_CLIENTS = 'authorizedClients',
  SHARES = 'shares',
  INBOX = 'inbox',
}

// Creiamo la versione debounced della funzione
const debouncedGetAllData = debounce(
  async (
    dataTypes: DataType[],
    resolve: (
      value: Partial<UserData> | PromiseLike<Partial<UserData>>
    ) => void,
    reject: (reason?: any) => void
  ) => {
    try {
      const typesToFetch = dataTypes || Object.values(DataType);
      const promises = typesToFetch.map((type) => dataFetchers[type]());
      const results = await Promise.allSettled(promises);

      const allUserData: Partial<UserData> = getUserData() || {
        configuration: undefined,
        files: undefined,
        basis: undefined,
        connected: undefined,
        authorizedClients: [],
        inbox: {
          grants: [],
          contents: [],
        },
        shares: {
          SecurePassword: [],
          SecureInBox: [],
          SecureVault: [],
          SecureSync: [],
        },
      };

      // Aggiorna solo i dati richiesti
      results.forEach((result, index) => {
        const currentType = typesToFetch[index];

        if (result.status === 'fulfilled') {
          switch (currentType) {
            case DataType.CONFIGURATION:
              allUserData.configuration = result.value as ConfigurationResponse;
              configurationSignal.value = result.value as ConfigurationResponse;
              break;
            case DataType.FILES:
              allUserData.files = result.value as VirtualFileSystemType;
              filesSignal.value = result.value as VirtualFileSystemType;
              break;
            case DataType.BASIS:
              allUserData.basis = { ...result.value } as Basis;
              setBasis(result.value as Basis);
              break;
            case DataType.CONNECTED:
              allUserData.connected = result.value as Connected;
              break;
            case DataType.AUTHORIZED_CLIENTS:
              allUserData.authorizedClients =
                result.value as AuthorizedClients[];
              break;
            case DataType.SHARES:
              allUserData.shares = result.value as Shares;
              break;
            case DataType.INBOX:
              allUserData.inbox = result.value as Inbox;
              break;
          }
        } else {
          console.error(`Failed to fetch ${currentType}:`, result.reason);
        }
      });

      setUserData(allUserData);
      resolve(allUserData);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      reject(null);
    }
  },
  3000
); // 1 secondo di debounce

export const getAllData = async (
  dataTypes: DataType[] = [
    DataType.CONFIGURATION,
    DataType.FILES,
    DataType.BASIS,
    DataType.CONNECTED,
    DataType.AUTHORIZED_CLIENTS,
    DataType.SHARES,
    DataType.INBOX,
  ]
) => {
  return new Promise((resolve, reject) => {
    debouncedGetAllData(dataTypes, resolve, reject);
  });
};

/**
 * Checks if the user is currently authenticated.
 * @returns {Promise<boolean>} A promise that resolves to true if the user is authenticated, false otherwise.
 */
const isAuthenticated = async (): Promise<boolean> => {
  try {
    return await API_CALL({
      url: '/authenticated',
      method: 'GET',
      skipError: true,
    })
      .then((response) => true)
      .catch(() => false);
  } catch (err) {
    console.error('Authentication check failed', err);
    throw new Error('Authentication check failed');
  }
};
const getBasis = async (): Promise<Basis> => {
  return API_CALL({ url: '/basis' }).then((basis: Basis) => {
    return basis;
  });
};

const getConnected = async (): Promise<Connected> => {
  return API_CALL({ url: '/clients/connected' }).then(
    (connected: Connected) => {
      return connected;
    }
  );
};

const getAuthorizedClients = async (): Promise<AuthorizedClients[]> => {
  return API_CALL({ url: '/clients?include_unlinked=true' }).then(
    (authorizedClients: AuthorizedClients[]) => {
      return authorizedClients;
    }
  );
};

const deleteAuthorizedClient = async (clientId: number): Promise<void> => {
  return API_CALL({ url: `/clients/${clientId}`, method: 'DELETE' });
};

const logout = async (): Promise<void> => {
  return API_CALL({ url: '/session', method: 'DELETE' }).catch((err) => {
    console.error('Logout failed', err);
  });
};

const saveCypherCode = async (cypherCode: string): Promise<void> => {
  setCypherCode(cypherCode);
};

const getCypherCode = async (): Promise<string> => {
  return libraryState.cypherCode.value || '';
};

const dataFetchers = {
  [DataType.CONFIGURATION]: configurationService.getConfiguration,
  [DataType.FILES]: FilesService.getVirtualFileSystem,
  [DataType.BASIS]: getBasis,
  [DataType.CONNECTED]: getConnected,
  [DataType.AUTHORIZED_CLIENTS]: getAuthorizedClients,
  [DataType.SHARES]: ShareService.getShares,
  [DataType.INBOX]: InboxService.getInboxes,
};

export const SessionService = {
  getSession,
  isAuthenticated,
  loginFlow,
  logout,
  loginSession,
  checkIsAuthenticated,
  getAllData,
  getBasis,
  getConnected,
  getUserData,
  deleteAuthorizedClient,
  saveCypherCode,
  getCypherCode,
};
